import { Button, Tooltip } from '@dixa/lib-shared-habla';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { shallowEqual } from 'react-redux';

import { ratingApi } from 'apis/rating';
import { satisfactionActions } from 'components/conversation/actions/satisfaction';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import { inferSurveyOfferStateFromConversationMessages } from './utils';

/**
 * Cancel survey action for the conversation action bar. When the survey has
 * already been triggered it shows a non-actionable state indicator
 * (Offered / Answered / Cancelled) instead of the cancel action.
 */
export const ConversationCancelSurveyButton = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const agentHasCsatPermission = useHasAccess('conversation.satisfaction');
  const agentHasReplyPermission = useHasAccess('conversation.reply');

  const [{ messages }, { id: csid, attributes }] = useAppSelector(
    (state) => [
      state.conversation.metaConversation,
      state.conversation.metaConversation.conversation,
    ],
    shallowEqual,
  );

  const { data: ratingInfoData } = ratingApi.useGetFutureRatingInfoQuery(
    csid ? { csid: csid.toString() } : skipToken,
  );

  const isCsatEligible = ratingInfoData?.data?.csat_eligible ?? false;
  const isUserExcluded = ratingInfoData?.data?.user_excluded_from_csat ?? false;
  const isCancellable = ratingInfoData?.data?.cancellable ?? true;

  // The survey falls back to English when a conversation language is set but
  // isn't among the survey's supported languages. When no language is set the
  // conversation already defaults to English, so no fallback is shown.
  const languageCode = attributes?.language;
  const surveyFallsBackToEnglish =
    languageCode != null &&
    !ratingInfoData?.data?.survey_languages.some(
      (code) => code === languageCode,
    );

  const surveyOfferState =
    inferSurveyOfferStateFromConversationMessages(messages);

  const handleCancelCsat = () => {
    ampli.conversationCsatMenuCancelOptionClicked();
    bindActionToPromise(
      dispatch,
      satisfactionActions.cancelSatisfactionRequest,
    )({ csid });
  };

  if (!isCsatEligible || isUserExcluded) {
    return null;
  }

  // Survey already triggered: show its state as a non-actionable indicator.
  if (surveyOfferState) {
    return (
      <Tooltip content="Customer satisfaction survey can no longer be modified">
        <Button variant="secondary" icon="smile" disabled>
          {surveyOfferState}
        </Button>
      </Tooltip>
    );
  }

  if (!isCancellable || !agentHasCsatPermission || !agentHasReplyPermission) {
    return null;
  }

  return (
    <Tooltip content="Cancel customer satisfaction survey for this conversation">
      <Button variant="secondary" icon="smile" onClick={handleCancelCsat}>
        Cancel survey{surveyFallsBackToEnglish ? ' (English)' : ''}
      </Button>
    </Tooltip>
  );
};
